
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/contexts/CommunityContext';

interface SearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
  initialValue?: string;
  variant?: 'default' | 'mobile';
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  onSearch,
  className = '',
  initialValue = '',
  variant = 'default'
}) => {
  const [query, setQuery] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentCommunity } = useCommunity();
  
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      } else {
        navigate(`/services?search=${encodeURIComponent(query.trim())}`);
      }
    }
  };
  
  const clearSearch = () => {
    setQuery('');
    if (onSearch) {
      onSearch('');
    }
  };

  return (
    <div className={`relative ${className}`}>
      <motion.form
        onSubmit={handleSearch}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`relative transition-all duration-300`}
      >
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={
            variant === 'mobile'
              ? "pl-4 pr-14 py-3 rounded-lg text-base placeholder:text-muted-foreground/60 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0 shadow-sm hover:shadow-md transition-all duration-300"
              : "pl-6 pr-16 py-7 rounded-full text-base md:text-lg placeholder:text-muted-foreground/60 bg-white/80 backdrop-blur-sm border-gray-200/60 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0 shadow-sm hover:shadow-md transition-all duration-300"
          }
          placeholder={placeholder || t('search.placeholder')}
          style={{ 
            borderColor: isFocused ? currentCommunity.primary_color : 'rgba(229, 231, 235, 0.6)',
            borderWidth: isFocused ? '2px' : '1px'
          }}
        />
        {query && (
          <motion.button
            type="button"
            onClick={clearSearch}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`absolute inset-y-0 ${variant === 'mobile' ? 'right-12' : 'right-14'} flex items-center pr-2`}
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
            </motion.div>
          </motion.button>
        )}
        <motion.button
          type="submit"
          className={`absolute inset-y-0 right-0 ${variant === 'mobile' ? 'pr-2' : 'pr-4'} flex items-center`}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-primary text-white ${variant === 'mobile' ? 'w-9 h-9' : 'w-10 h-10'} rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300`}
          >
            <Search className={`${variant === 'mobile' ? 'h-4 w-4' : 'h-5 w-5'}`} />
          </motion.div>
        </motion.button>
      </motion.form>
    </div>
  );
};

export default SearchBar;
